import React, { createContext, useContext, useCallback, useEffect } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useSelector } from "react-redux";

export interface ISelectedBranchesMap {
  pickup_branch_ids: Record<string, boolean>;
  delivery_branch_ids: Record<string, boolean>;
  drive_thru_branch_ids?: Record<string, boolean>;
}

export type FormValues = {
  selectedBranchesMap: ISelectedBranchesMap;
};

interface IPickupBranchesFormContextValue {
  form: UseFormReturn<FormValues>;
  selectedBranchesMap: ISelectedBranchesMap;
  setFieldValue: (field: keyof ISelectedBranchesMap, branchId: string, value: boolean) => void;
  resetAllFields: () => void;
}

const PickupBranchesFormContext = createContext<IPickupBranchesFormContextValue | null>(null);

export const usePickupBranchesForm = () => {
  const ctx = useContext(PickupBranchesFormContext);
  if (!ctx) throw new Error("usePickupBranchesForm must be used within PickupBranchesFormProvider");
  return ctx;
};

interface IProviderProps {
  children: React.ReactNode;
}

const EMPTY_MAP: ISelectedBranchesMap = {
  pickup_branch_ids: {},
  delivery_branch_ids: {},
  drive_thru_branch_ids: {},
};

export const PickupBranchesFormProvider: React.FC<IProviderProps> = ({ children }) => {
  const savedMap = useSelector(
    (state: any) => state.selectedBranchesMapReducer
  ) as ISelectedBranchesMap | null;

  const form = useForm<FormValues>({
    defaultValues: {
      selectedBranchesMap: savedMap || EMPTY_MAP,
    },
  });

  const selectedBranchesMap = form.watch("selectedBranchesMap");

  useEffect(() => {
    if (savedMap && Object.keys(savedMap.pickup_branch_ids).length > 0) {
      if (form.formState.isDirty) {
        return;
      }
      form.reset({ selectedBranchesMap: savedMap });
    }
  }, [savedMap, form]);

  const setFieldValue = useCallback(
    (field: keyof ISelectedBranchesMap, branchId: string, value: boolean) => {
      const current = form.getValues("selectedBranchesMap");
      const updated = {
        ...current,
        [field]: {
          ...(current[field] || {}),
          [branchId]: value,
        },
      };
      form.setValue("selectedBranchesMap", updated, { shouldDirty: true });
    },
    [form]
  );

  const resetAllFields = useCallback(() => {
    form.setValue("selectedBranchesMap", {
      pickup_branch_ids: {},
      delivery_branch_ids: {},
      drive_thru_branch_ids: {},
    });
  }, [form]);

  const value: IPickupBranchesFormContextValue = {
    form,
    selectedBranchesMap,
    setFieldValue,
    resetAllFields,
  };

  return (
    <PickupBranchesFormContext.Provider value={value}>
      {children}
    </PickupBranchesFormContext.Provider>
  );
};

export default PickupBranchesFormContext;
